<template>
  <div class="hs-code__search-box">
    <div class="hs-code__search-wrap">
      <font-awesome-icon icon="search" class="hs-code__search-icon" />
      <input
        type="search"
        :placeholder="$t('Enter a description')"
        class="hs-code__input-field"
        autocapitalize="none"
        v-model="searchQuery"
        @keyup="triggerSearch"
        @click="toggleSearch(true)"
      />
    </div>

    <div
      class="hs-code__results-wrap"
      @click="toggleSearch(false)"
      v-if="searchQuery && searchResults.length > 0 && showSearchResults"
    >
      <router-link
        class="hs-code__results-item hs-code__results-link"
        :to="result.url"
        v-for="(result, index) in searchResults"
        :ref="`result-${index}`"
        :key="`${result.code}-${index}`"
      >
        <text-highlight :queries="searchQuery">{{
          result.description
        }}</text-highlight>
      </router-link>
    </div>
    <div class="hs-code__results-wrap" v-if="isSearching && searchQuery">
      <div class="hs-code__searching-state">
        {{ $t("Searching the HS database...") }} 🧐
      </div>
    </div>
    <div
      class="hs-code__results-wrap"
      v-if="
        !isSearching &&
        searchQuery &&
        searchResults.length === 0 &&
        showSearchResults
      "
    >
      <div class="hs-code__searching-state">
        {{ $t("We couldn't find anything. Maybe try a broader description?") }}
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";

import TextHighlight from "vue-text-highlight";
import { getCurrentLanguage } from "@/lang/i18n.js";
import algoliasearch from "algoliasearch/lite";

const client = algoliasearch("LDQWVJ2M2L", "ffa428000a819f23a89250f2285b41bb");
const algoliaIndex = client.initIndex("codes");

export default {
  name: "HSSearch",
  components: {
    TextHighlight,
  },
  props: ["language"],
  data() {
    return {
      hsCode: null,
      searchQuery: null,
      showSearchResults: false,
      isSearching: false,
      searchResults: [],
      airtableBase: {},
      selectedCode: null,
      selectedChapter: null,
      selectedHeading: null,
      selectedChapterDescription: null,
      selectedHeadingDescription: null,
      selectedSubheadingDescription: null,
      selectedCodeDescription: null,
      selectedSubheading: null,
      currentLanguage: getCurrentLanguage(),
      currentKeyIndex: -1,
    };
  },

  methods: {
    triggerSearch() {
      this.isSearching = true;
      this.searchResults = [];
      this.debounceAndSearch();
    },
    debounceAndSearch: _.debounce(
      function () {
        this.search();
      },
      250,
      {
        trailing: true,
        leading: true,
      }
    ),
    toggleSearch(val) {
      this.showSearchResults = val;
    },
    async search() {
      if (!this.searchQuery || this.searchQuery.length < 3) return;

      const searchLanguage =
        this.language !== "en" ? `i18n.${this.language}` : "name";

      const algoliaResults = await algoliaIndex.search(this.searchQuery, {
        attributesToRetrieve: [searchLanguage, "code"],
        restrictSearchableAttributes: [searchLanguage],
      });

      this.searchResults = algoliaResults.hits.map((hit) => {
        return {
          description: hit.i18n ? hit.i18n[this.language] : hit.name,
          code: hit.code,
          url: `/hs-codes/codes/${hit.code}`,
        };
      });
      this.isSearching = false;
    },
    copyCode() {
      const codeEl = this.$refs["code"];
      codeEl.select();
      document.execCommand("copy");
    },
  },
};
</script>

<style scoped>
.hs-code__search-box {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin: 30px 0px;
  margin-bottom: 15px;
  background: rgb(255, 255, 255);

  border-radius: 15px;
  overflow: hidden;
}

.hs-code__search-box:hover {
  box-shadow: 0px 0px 8px 0px rgba(6, 27, 36, 0.2);
}

.hs-code__search-icon {
  pointer-events: none;
  position: absolute;
  margin: -1px 0px 0px 15px;
  font-size: 16px;
}
.hs-code__input-field {
  width: 100%;
  height: 45px;
  padding: 0px 15px 0px 40px;
  background: rgb(240, 244, 246);
  color: #122857;
  border-radius: 15px;
  border: none;
}

.hs-code__input-field::placeholder {
  color: #1238579a;
}

.hs-code__input-field:focus {
  outline: none;
  box-shadow: inset 0px 0px 0px 3px rgba(83, 181, 226, 0.3);
}

.hs-code__search-wrap {
  display: flex;
  flex-direction: row;
  flex: 0 0 auto;
  align-self: stretch;
  align-items: center;
  margin: 0px 0px 0px 0px;
  background: rgb(255, 255, 255);
  overflow: hidden;
}
.hs-code__searching-state {
  padding: 60px 0px;
  background: ;
  border-top: 1px solid #f1f1f1;
  border-bottom: none;
  transition: background 0.25s ease-in-out;
  font-size: 0.9em;
  line-height: 1.2em;
  text-align: center;
}
.hs-code__results-wrap {
  display: flex;
  flex-direction: column;
  min-height: 0px;
  padding: 0px;
  background: rgb(255, 255, 255);
}

.hs-code__results-item {
  padding: 15px;
  text-align: left;
  border: none;
  border-top: 1px solid #f1f1f1;
  transition: background 0.15s ease-in-out;
  font-size: 0.9em;
  line-height: 1.5em;
}

.hs-code__results-item:first-child {
  border-top: none;
}

.hs-code__results-link {
  color: inherit;
}

.hs-code__results-item:hover {
  background: rgba(240, 244, 246, 0.5);
}

.hs-code__results-item:focus {
  outline: none;
  background: rgb(240, 244, 246);
  font-weight: 800;
}
</style>
